import { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import {
  Users,
  CheckCircle,
  DollarSign,
  CreditCard,
  AlertTriangle,
  XCircle,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react"
import { getStats } from "../../api/dashboard"
import { getMemberships, updateMembership } from "../../api/memberships"
import { getPayments, createPayment, updatePayment, deletePayment } from "../../api/payments"
import { deleteClient } from "../../api/clients"
import type { DashboardStats, Membership, Payment } from "../../types"

function formatCRC(amount: number) {
  return new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    minimumFractionDigits: 0,
  }).format(amount)
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

const PLANS = [
  {
    value: "bronze",
    label: "1 sesión/semana",
    sublabel: "Clase Grupal",
    price: 29900,
    color: "text-amber-400",
    bg: "bg-amber-400/10 border-amber-400/30",
  },
  {
    value: "pro",
    label: "2 sesiones/semana",
    sublabel: "Clase Grupal",
    price: 47900,
    color: "text-primary",
    bg: "bg-primary/10 border-primary/30",
  },
  {
    value: "gold",
    label: "3 sesiones/semana",
    sublabel: "Clase Grupal",
    price: 65900,
    color: "text-yellow-400",
    bg: "bg-yellow-400/10 border-yellow-400/30",
  },
  {
    value: "platinum",
    label: "4 sesiones/semana",
    sublabel: "Clase Grupal",
    price: 75900,
    color: "text-purple-400",
    bg: "bg-purple-400/10 border-purple-400/30",
  },
  {
    value: "elite",
    label: "5 sesiones/semana",
    sublabel: "Clase Grupal",
    price: 99900,
    color: "text-emerald-400",
    bg: "bg-emerald-400/10 border-emerald-400/30",
  },
  {
    value: "personal_training",
    label: "Entrenamiento Personal",
    sublabel: "por cita",
    price: 15000,
    color: "text-blue-400",
    bg: "bg-blue-400/10 border-blue-400/30",
  },
  {
    value: "virtual",
    label: "Asesoría/Entrenamiento Virtual",
    sublabel: "mensual",
    price: 35000,
    color: "text-cyan-400",
    bg: "bg-cyan-400/10 border-cyan-400/30",
  },
  {
    value: "regular",
    label: "Personalizado",
    sublabel: "sin plan",
    price: 0,
    color: "text-slate-300",
    bg: "bg-slate-400/10 border-slate-400/30",
  },
]

function getPlanStyle(plan: string) {
  return PLANS.find((p) => p.value === plan) ?? PLANS[0]
}

function formatPaymentDate(dateStr: string): string {
  if (!dateStr) return ""
  const [y, m, d] = dateStr.split("T")[0].split("-")
  return `${d}/${m}/${y}`
}

// Vencimiento "real" de una membresía: null cuando no aplica (plan regular).
function expiryOf(m: Membership): string | null {
  return m.plan === "regular" ? null : m.end_date || null
}

function isExpiringSoon(endDate: string): boolean {
  if (!endDate) return false
  const end = new Date(endDate.split("T")[0])
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.floor((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  return diffDays <= 7
}

// ── Modal Editar Método de Pago ──────────────────────────────────────────────
const METHODS = ["cash", "transfer", "SINPE", "card"]

function EditPaymentModal({
  payment,
  onClose,
  onUpdated,
}: {
  payment: Payment
  onClose: () => void
  onUpdated: (p: Payment) => void
}) {
  const [method, setMethod] = useState(payment.method)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const updated = await updatePayment(payment.id, { method })
      onUpdated(updated)
    } catch {
      setError("Error al actualizar el método de pago.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-4 w-full max-w-sm rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Editar Método de Pago</h2>
            <p className="text-xs text-muted">{payment.user?.name ?? `User #${payment.user_id}`}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-white transition text-xl leading-none"
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">
              Método de Pago
            </label>
            <div className="grid grid-cols-4 gap-2">
              {METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`rounded-lg border px-2 py-2 text-xs font-medium transition capitalize ${
                    method === m
                      ? "border-primary bg-primary text-black"
                      : "border-white/10 bg-surface-elevated text-muted hover:border-primary/50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {error && (
            <p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// ── Modal Registrar Pago ────────────────────────────────────────────────────
function AddPaymentModal({
  memberships,
  onClose,
  onAdded,
}: {
  memberships: Membership[]
  onClose: () => void
  onAdded: (p: Payment) => void
}) {
  // Sin cliente preseleccionado: el pago exige buscar y elegir explícitamente
  // (antes se preseleccionaba el primero y era fácil cobrarle por error).
  const [membershipId, setMembershipId] = useState<number>(0)
  const [amount, setAmount] = useState<string>("")
  const [query, setQuery] = useState<string>("")
  const [open, setOpen] = useState(false)
  const [method, setMethod] = useState("cash")
  const [date, setDate] = useState(() => {
    const t = new Date()
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function membershipLabel(m: Membership) {
    const name = m.user?.name ?? `User #${m.user_id}`
    return `${name} — ${m.plan}${m.status !== "active" ? " (inactivo)" : ""}`
  }

  function selectMembership(m: Membership) {
    setMembershipId(m.id)
    setAmount(String(m.price))
    setQuery(m.user?.name ?? `User #${m.user_id}`)
    setOpen(false)
    setError(null)
  }

  // Búsqueda insensible a mayúsculas y tildes ("matias" encuentra "Matías")
  function normalize(s: string) {
    return s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
  }

  const filteredMemberships = memberships.filter((m) => {
    const name = m.user?.name ?? `User #${m.user_id}`
    return normalize(name).includes(normalize(query.trim()))
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!membershipId) {
      setError("Buscá y seleccioná un cliente de la lista.")
      return
    }
    if (!amount || Number(amount) <= 0) {
      setError("Ingresá un monto mayor a 0.")
      return
    }
    const selectedMembership = memberships.find((m) => m.id === membershipId)
    setSaving(true)
    setError(null)
    try {
      const payment = await createPayment({
        user_id: selectedMembership?.user_id,
        membership_id: membershipId,
        amount: Number(amount),
        payment_date: date,
        method,
        status: "completed",
      })
      onAdded(payment)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0]
      setError(msg ?? "Error al registrar el pago.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-4 w-full max-w-md rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Registrar Pago</h2>
            <p className="text-xs text-muted">Ingresa el pago del cliente</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-white transition text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">
              Cliente
            </label>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                // Al escribir se invalida la selección anterior: obliga a elegir
                // de la lista y evita registrar el pago al cliente equivocado.
                setQuery(e.target.value)
                setMembershipId(0)
                setOpen(true)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  if (filteredMemberships.length > 0) selectMembership(filteredMemberships[0])
                }
              }}
              onFocus={() => setOpen(true)}
              onBlur={() =>
                setTimeout(() => {
                  setOpen(false)
                  const sel = memberships.find((m) => m.id === membershipId)
                  if (sel) setQuery(sel.user?.name ?? `User #${sel.user_id}`)
                }, 150)
              }
              placeholder="Buscar cliente por nombre..."
              autoComplete="off"
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
            {open && (
              <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-white/10 bg-surface-elevated shadow-2xl">
                {filteredMemberships.length === 0 && (
                  <li className="px-3 py-2 text-xs text-muted">Sin resultados</li>
                )}
                {filteredMemberships.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        selectMembership(m)
                      }}
                      className={`w-full px-3 py-2 text-left text-sm transition hover:bg-white/5 ${
                        m.id === membershipId ? "text-primary" : "text-white"
                      }`}
                    >
                      {membershipLabel(m)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">
              Monto (CRC)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">
              Método de Pago
            </label>
            <div className="grid grid-cols-4 gap-2">
              {["cash", "transfer", "SINPE", "card"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`rounded-lg px-2 py-2 text-xs font-medium transition ${
                    method === m
                      ? "bg-primary text-black font-semibold glow-cyan"
                      : "border border-white/10 text-muted hover:text-white hover:border-white/30"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">
              Fecha
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          {error && (
            <p className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Registrar Pago"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// ── Modal Editar Membresía ─────────────────────────────────────────────────
function EditMembershipModal({
  membership,
  onClose,
  onUpdated,
}: {
  membership: Membership
  onClose: () => void
  onUpdated: (m: Membership) => void
}) {
  const [plan, setPlan] = useState(membership.plan)
  const [status, setStatus] = useState<Membership["status"]>(membership.status)
  const [price, setPrice] = useState(String(membership.price))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const updated = await updateMembership(membership.id, { plan, status, price: Number(price) })
      onUpdated({ ...updated, user: membership.user })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0]
      setError(msg ?? "Error al actualizar la membresía.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-4 w-full max-w-md rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Editar Membresía</h2>
            <p className="text-xs text-muted">
              {membership.user?.name ?? `User #${membership.user_id}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-white transition text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">
              Plan
            </label>
            <div className="space-y-2">
              <p className="text-xs text-muted">
                Clases Grupales <span className="opacity-60">(precio mensual)</span>
              </p>
              {PLANS.filter((p) =>
                ["bronze", "pro", "gold", "platinum", "elite"].includes(p.value),
              ).map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => {
                    setPlan(p.value)
                    setPrice(String(p.price))
                  }}
                  className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                    plan === p.value
                      ? `${p.bg} ${p.color}`
                      : "border-white/10 text-muted hover:text-white hover:border-white/20"
                  }`}
                >
                  <span className="font-medium">{p.label}</span>
                  <span className={plan === p.value ? p.color : "text-muted"}>
                    {formatCRC(p.price)}
                  </span>
                </button>
              ))}
              <div className="border-t border-white/10 pt-2 space-y-2">
                {PLANS.filter((p) => ["personal_training", "virtual"].includes(p.value)).map(
                  (p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => {
                        setPlan(p.value)
                        setPrice(String(p.price))
                      }}
                      className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                        plan === p.value
                          ? `${p.bg} ${p.color}`
                          : "border-white/10 text-muted hover:text-white hover:border-white/20"
                      }`}
                    >
                      <span className="font-medium">
                        {p.label} <span className="text-xs opacity-60">{p.sublabel}</span>
                      </span>
                      <span className={plan === p.value ? p.color : "text-muted"}>
                        {formatCRC(p.price)}
                        {p.value === "personal_training" ? "/cita" : ""}
                      </span>
                    </button>
                  ),
                )}
                <button
                  type="button"
                  onClick={() => setPlan("regular")}
                  className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                    plan === "regular"
                      ? "bg-slate-400/10 border-slate-400/30 text-slate-300"
                      : "border-white/10 text-muted hover:text-white hover:border-white/20"
                  }`}
                >
                  <span className="font-medium">
                    Personalizado <span className="text-xs opacity-60">precio libre</span>
                  </span>
                  <span className={plan === "regular" ? "text-slate-300" : "text-muted"}>
                    Definí el monto ↓
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">
              Precio Mensual (CRC)
            </label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">
              Estado
            </label>
            <div className="flex gap-2">
              {(["active", "suspended", "inactive"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium capitalize transition ${
                    status === s
                      ? s === "active"
                        ? "bg-success/20 border border-success text-success"
                        : s === "suspended"
                          ? "bg-warning/20 border border-warning text-warning"
                          : "bg-danger/20 border border-danger text-danger"
                      : "border border-white/10 text-muted hover:text-white"
                  }`}
                >
                  {s === "active" ? "Activo" : s === "suspended" ? "Suspendido" : "Inactivo"}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// ── KPI Card ───────────────────────────────────────────────────────────────
function KpiCard({
  icon: Icon,
  label,
  value,
  index,
  accent,
}: {
  icon: React.ElementType
  label: string
  value: string | number
  index: number
  accent?: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="gradient-card border border-white/10 rounded-xl p-5 border-glow"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-muted uppercase tracking-wide">{label}</p>
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon size={16} className={accent ?? "text-primary"} />
        </div>
      </div>
      <p className={`text-2xl font-bold ${accent ?? "text-primary"} animate-pulse-glow`}>{value}</p>
    </motion.div>
  )
}

// ── Modal Eliminar Cliente (CRM) ───────────────────────────────────────────
function DeleteClientModal({
  membership,
  onClose,
  onDeleted,
}: {
  membership: Membership
  onClose: () => void
  onDeleted: () => void
}) {
  const name = membership.user?.name ?? `User #${membership.user_id}`
  const [confirm, setConfirm] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isConfirmed = confirm.trim().toLowerCase() === name.trim().toLowerCase()

  async function handleDelete() {
    if (!isConfirmed) return
    setDeleting(true)
    setError(null)
    try {
      await deleteClient(membership.user_id)
      onDeleted()
    } catch {
      setError("No se pudo eliminar el cliente. Intentá de nuevo.")
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl border border-danger/30 gradient-card p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger/10 border border-danger/20">
            <AlertTriangle size={18} className="text-danger" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Eliminar cliente</h2>
            <p className="text-xs text-muted mt-0.5">
              Se eliminarán todos los datos asociados a{" "}
              <span className="text-white font-medium">{name}</span>. Esta acción es irreversible.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-muted hover:text-white transition text-xl shrink-0"
          >
            &times;
          </button>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-muted">
            Escribí el nombre del cliente para confirmar:
            <span className="ml-1 font-semibold text-white">{name}</span>
          </p>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Nombre del cliente"
            autoFocus
            className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-danger"
          />
          {error && (
            <p className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button
            onClick={handleDelete}
            disabled={!isConfirmed || deleting}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-danger py-2.5 text-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-40"
          >
            <Trash2 size={14} />
            {deleting ? "Eliminando..." : "Eliminar cliente"}
          </button>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
          >
            Cancelar
          </button>
        </div>
      </motion.div>
    </div>
  )
}

// ── Main CRM Tab ───────────────────────────────────────────────────────────
export default function CrmTab() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [payments, setPayments] = useState<Payment[]>([])
  const [showAddPayment, setShowAddPayment] = useState(false)
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null)
  const [editingMembership, setEditingMembership] = useState<Membership | null>(null)
  const [deletingMembership, setDeletingMembership] = useState<Membership | null>(null)

  useEffect(() => {
    getStats()
      .then(setStats)
      .catch(() => {})
    getMemberships()
      .then((data) => setMemberships(Array.isArray(data) ? data : []))
      .catch(() => setMemberships([]))
    getPayments()
      .then((data) => setPayments(Array.isArray(data) ? data : []))
      .catch(() => setPayments([]))
  }, [])

  const pending = stats ? stats.expected_revenue - stats.collected_this_month : 0

  // Vencidas o por vencer primero (end_date ascendente); sin vencimiento al
  // final — no están "por vencer". El plan "regular" (Personalizado, sin plan)
  // nunca vence solo (el backend lo excluye de la auto-desactivación), así que
  // su end_date es relleno y se trata como ausente: ni se ordena por él ni se
  // muestra. end_date es columna date de Rails (YYYY-MM-DD, sin hora/timezone),
  // así que comparar como string es seguro y más simple que parsear Date. Se
  // recalcula solo cuando cambia `memberships`, así que sube/baja sola cuando
  // se registra un pago (el pago ya extiende end_date un mes).
  const sortedMemberships = useMemo(() => {
    return [...memberships].sort((a, b) => {
      const ea = expiryOf(a)
      const eb = expiryOf(b)
      if (!ea && !eb) return 0
      if (!ea) return 1
      if (!eb) return -1
      return ea < eb ? -1 : ea > eb ? 1 : 0
    })
  }, [memberships])

  return (
    <div className="space-y-6">
      {showAddPayment && (
        <AddPaymentModal
          memberships={memberships}
          onClose={() => setShowAddPayment(false)}
          onAdded={(p) => {
            setPayments((prev) => [p, ...prev])
            setShowAddPayment(false)
            getStats().then(setStats)
            getMemberships().then((data) => setMemberships(Array.isArray(data) ? data : []))
          }}
        />
      )}

      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          onClose={() => setEditingPayment(null)}
          onUpdated={(updated) => {
            setPayments((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
            setEditingPayment(null)
          }}
        />
      )}

      {editingMembership && (
        <EditMembershipModal
          membership={editingMembership}
          onClose={() => setEditingMembership(null)}
          onUpdated={(updated) => {
            setMemberships((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))
            setEditingMembership(null)
            getStats().then(setStats)
          }}
        />
      )}

      {deletingMembership && (
        <DeleteClientModal
          membership={deletingMembership}
          onClose={() => setDeletingMembership(null)}
          onDeleted={() => {
            setMemberships((prev) => prev.filter((m) => m.id !== deletingMembership.id))
            setPayments((prev) => prev.filter((p) => p.user_id !== deletingMembership.user_id))
            setDeletingMembership(null)
            getStats().then(setStats)
          }}
        />
      )}

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard icon={Users} label="Total Clientes" value={stats.total_clients} index={0} />
          <KpiCard
            icon={CheckCircle}
            label="Activos"
            value={stats.active_clients}
            index={1}
            accent="text-success"
          />
          <KpiCard
            icon={DollarSign}
            label="Ingreso Esperado"
            value={formatCRC(stats.expected_revenue)}
            index={2}
          />
          <KpiCard
            icon={CreditCard}
            label="Recaudado este Mes"
            value={formatCRC(stats.collected_this_month)}
            index={3}
            accent="text-success"
          />
        </div>
      )}

      {/* Alerta de pendientes */}
      {pending > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-3 rounded-lg bg-warning/10 border border-warning/30 px-4 py-3"
        >
          <AlertTriangle size={16} className="text-warning shrink-0" />
          <span className="text-sm text-warning">
            Pendiente: <strong>{formatCRC(pending)}</strong> por recaudar este mes
          </span>
        </motion.div>
      )}

      {/* Client CRM Cards */}
      <div>
        <h3
          className="mb-4 text-sm font-semibold text-white uppercase tracking-widest"
          style={{ fontFamily: "var(--font-display)" }}
        >
          CRM de Clientes
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sortedMemberships.map((m, i) => {
            const planStyle = getPlanStyle(m.plan)
            const name = m.user?.name ?? `User #${m.user_id}`
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="gradient-card border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-sm font-bold">
                      {getInitials(name)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{name}</p>
                      <span className={`text-xs font-medium ${planStyle.color}`}>
                        {planStyle.label} — {planStyle.sublabel}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      m.status === "active"
                        ? "bg-success/20 text-success"
                        : m.status === "suspended"
                          ? "bg-warning/20 text-warning"
                          : "bg-danger/20 text-danger"
                    }`}
                  >
                    {m.status === "active"
                      ? "Activo"
                      : m.status === "suspended"
                        ? "Suspendido"
                        : "Inactivo"}
                  </span>
                </div>

                {expiryOf(m) && (
                  <div
                    className={`mb-2 text-xs flex items-center gap-1 ${
                      m.status === "inactive"
                        ? "text-danger/70"
                        : isExpiringSoon(m.end_date)
                          ? "text-warning"
                          : "text-muted"
                    }`}
                  >
                    <span>Vence: {formatPaymentDate(m.end_date)}</span>
                    {m.status === "active" && isExpiringSoon(m.end_date) && (
                      <AlertTriangle size={11} className="text-warning" />
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-white">{formatCRC(m.price)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingMembership(m)}
                      className="flex items-center gap-1 text-xs text-muted hover:text-primary transition-colors"
                    >
                      <Pencil size={12} />
                      Editar
                    </button>
                    <button
                      onClick={() => setDeletingMembership(m)}
                      className="flex items-center gap-1 text-xs text-muted hover:text-danger transition-colors"
                      title="Eliminar cliente"
                    >
                      <Trash2 size={12} />
                      Eliminar
                    </button>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* Pagos Recientes */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3
            className="text-sm font-semibold text-white uppercase tracking-widest"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Pagos Recientes
          </h3>
          <button
            onClick={() => setShowAddPayment(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-black hover:opacity-90 transition glow-cyan"
          >
            <Plus size={14} />
            Registrar Pago
          </button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10 gradient-card">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10">
              <tr>
                {["Cliente", "Monto", "Fecha", "Método", ""].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wide"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-white/5 hover:bg-white/2 transition-colors"
                >
                  <td className="px-4 py-3 text-white font-medium">
                    {p.user?.name ?? `User #${p.user_id}`}
                  </td>
                  <td className="px-4 py-3 text-success font-semibold">{formatCRC(p.amount)}</td>
                  <td className="px-4 py-3 text-muted">{formatPaymentDate(p.payment_date)}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-xs text-muted capitalize">
                      {p.method}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingPayment(p)}
                        className="flex items-center gap-1 text-xs text-muted hover:text-primary transition-colors"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={async () => {
                          if (!confirm("¿Eliminar este registro de pago?")) return
                          await deletePayment(p.id)
                          setPayments((prev) => prev.filter((x) => x.id !== p.id))
                          getStats().then(setStats)
                        }}
                        className="flex items-center gap-1 text-xs text-muted hover:text-danger transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-xs text-muted">
                    <XCircle size={24} className="mx-auto mb-2 opacity-30" />
                    No hay pagos registrados aún.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
